import React, { useState, useEffect, useCallback } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import {
    Box, Typography, Paper, Button, Chip, Switch, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Alert, CircularProgress,
    IconButton, Tooltip, Tabs, Tab, Slider, useTheme, FormControl,
    InputLabel, Select, MenuItem, RadioGroup, FormControlLabel, Radio,
    InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
    CheckCircle as ActiveIcon, Cancel as DisabledIcon,
    Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
    Save as SaveIcon,
    PersonAddAlt1 as PromoteIcon,
    PersonRemove as DemoteIcon,
    GroupAdd as TeamIcon,
    Edit as EditIcon,
    LockReset as ResetMfaIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import { toServerDate } from '../../timezone';

const CAPTCHA_PROVIDERS = [
    { value: 'RECAPTCHA_V2', label: 'reCAPTCHA v2' },
    { value: 'RECAPTCHA_V3', label: 'reCAPTCHA v3' },
    { value: 'HCAPTCHA', label: 'hCaptcha' },
    { value: 'TURNSTILE', label: 'Cloudflare Turnstile' },
    { value: 'FRIENDLY_CAPTCHA', label: 'Friendly Captcha' },
];

const SecurityPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [tab, setTab] = useState(0);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Captcha state
    const [captcha, setCaptcha] = useState({ provider: 'RECAPTCHA_V2', siteKey: '', secretKey: '', enabled: false, minScore: 0.5 });
    const [captchaLoading, setCaptchaLoading] = useState(true);
    const [captchaSaving, setCaptchaSaving] = useState(false);
    const [showSecret, setShowSecret] = useState(false);

    // Operators state
    const [operators, setOperators] = useState([]);
    const [operatorsLoading, setOperatorsLoading] = useState(true);
    const [createOpOpen, setCreateOpOpen] = useState(false);
    const [newOpMode, setNewOpMode] = useState('manual');
    const [newOp, setNewOp] = useState({ username: '', email: '', password: '' });
    const [deleteOpOpen, setDeleteOpOpen] = useState(false);
    const [deleteOp, setDeleteOp] = useState(null);
    // Edit operator (email / password) + reset-authenticator confirm. Shared
    // across full operators and website operators (keyed by row.role).
    const [editOp, setEditOp] = useState(null);
    const [editOpForm, setEditOpForm] = useState({ email: '', password: '' });
    const [editShowPw, setEditShowPw] = useState(false);
    const [mfaResetOp, setMfaResetOp] = useState(null);

    // Website Operators state — sub-operator role scoped to the default
    // org's website surface (Waitlist, Documentation, Contact Inbox).
    const [websiteOps, setWebsiteOps] = useState([]);
    const [websiteOpsLoading, setWebsiteOpsLoading] = useState(true);
    const [createWebOpOpen, setCreateWebOpOpen] = useState(false);
    // Three modes the dialog can operate in: 'promote' picks an
    // existing default-org user; 'manual' creates a fresh account
    // with a chosen password; 'invite' creates a fresh account and
    // mails them a set-password link.
    const [newWebOpMode, setNewWebOpMode] = useState('promote');
    const [newWebOp, setNewWebOp] = useState({ username: '', email: '', password: '' });
    const [promoteUserId, setPromoteUserId] = useState('');
    const [websiteCandidates, setWebsiteCandidates] = useState([]);
    const [candidatesLoading, setCandidatesLoading] = useState(false);
    const [deleteWebOpOpen, setDeleteWebOpOpen] = useState(false);
    const [deleteWebOp, setDeleteWebOp] = useState(null);
    const [demoteWebOpOpen, setDemoteWebOpOpen] = useState(false);
    const [demoteWebOp, setDemoteWebOp] = useState(null);

    const loadCaptcha = useCallback(async () => {
        setCaptchaLoading(true);
        try {
            const res = await billingService.getCaptchaSettings();
            setCaptcha(res.data || { provider: 'RECAPTCHA_V2', siteKey: '', secretKey: '', enabled: false, minScore: 0.5 });
        } catch {
            // No settings yet, use defaults
        } finally {
            setCaptchaLoading(false);
        }
    }, []);

    const loadOperators = useCallback(async () => {
        setOperatorsLoading(true);
        try {
            const res = await billingService.listOperators();
            setOperators(res.data || []);
        } catch {
            setMessage({ type: 'error', text: 'Failed to load operators' });
        } finally {
            setOperatorsLoading(false);
        }
    }, []);

    const loadWebsiteOps = useCallback(async () => {
        setWebsiteOpsLoading(true);
        try {
            const res = await billingService.listWebsiteOperators();
            setWebsiteOps(res.data || []);
        } catch {
            setMessage({ type: 'error', text: 'Failed to load website operators' });
        } finally {
            setWebsiteOpsLoading(false);
        }
    }, []);

    const loadCandidates = useCallback(async () => {
        setCandidatesLoading(true);
        try {
            const res = await billingService.listWebsiteOperatorCandidates();
            setWebsiteCandidates(res.data || []);
        } catch {
            setWebsiteCandidates([]);
        } finally {
            setCandidatesLoading(false);
        }
    }, []);

    useEffect(() => { loadCaptcha(); loadOperators(); loadWebsiteOps(); }, [loadCaptcha, loadOperators, loadWebsiteOps]);

    // Captcha handlers
    const handleSaveCaptcha = async () => {
        setCaptchaSaving(true);
        try {
            await billingService.updateCaptchaSettings(captcha);
            setMessage({ type: 'success', text: 'Captcha settings saved' });
            loadCaptcha();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save captcha settings' });
        } finally {
            setCaptchaSaving(false);
        }
    };

    // Operator handlers
    const handleCreateOperator = async () => {
        if (!newOp.username) {
            setMessage({ type: 'error', text: 'Username is required' });
            return;
        }
        if (newOpMode === 'invite' && !newOp.email) {
            setMessage({ type: 'error', text: 'Email is required for invitation' });
            return;
        }
        if (newOpMode === 'manual' && (!newOp.password || newOp.password.length < 6)) {
            setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
            return;
        }
        try {
            await billingService.createOperator({
                mode: newOpMode,
                username: newOp.username,
                email: newOp.email || undefined,
                password: newOpMode === 'manual' ? newOp.password : undefined,
            });
            setMessage({ type: 'success', text: `Operator "${newOp.username}" created` });
            setCreateOpOpen(false);
            setNewOp({ username: '', email: '', password: '' });
            setNewOpMode('manual');
            loadOperators();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to create operator' });
        }
    };

    const handleToggleOperator = async (op) => {
        try {
            await billingService.updateOperator(op.id, { enabled: !op.enabled });
            loadOperators();
        } catch {
            setMessage({ type: 'error', text: 'Failed to update operator' });
        }
    };

    const handleDeleteOperator = async () => {
        if (!deleteOp) return;
        try {
            await billingService.deleteOperator(deleteOp.id);
            setMessage({ type: 'success', text: 'Operator deleted' });
            setDeleteOpOpen(false);
            setDeleteOp(null);
            loadOperators();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete operator' });
        }
    };

    // Open the edit dialog for either operator flavour (keyed by role).
    const openEditOperator = (op) => {
        setEditOp(op);
        setEditOpForm({ email: op.email || '', password: '' });
        setEditShowPw(false);
    };

    const isWebOp = (op) => op?.role === 'WEB_ADMIN';

    const handleSaveOperator = async () => {
        if (!editOp) return;
        const data = {};
        if ((editOpForm.email || '') !== (editOp.email || '')) data.email = editOpForm.email;
        if (editOpForm.password) {
            if (editOpForm.password.length < 6) {
                setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
                return;
            }
            data.password = editOpForm.password;
        }
        if (Object.keys(data).length === 0) { setEditOp(null); return; }
        try {
            if (isWebOp(editOp)) await billingService.updateWebsiteOperator(editOp.id, data);
            else await billingService.updateOperator(editOp.id, data);
            setMessage({ type: 'success', text: `Updated "${editOp.username}"` });
            setEditOp(null);
            loadOperators();
            loadWebsiteOps();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update operator' });
        }
    };

    const handleResetMfa = async () => {
        if (!mfaResetOp) return;
        try {
            if (isWebOp(mfaResetOp)) await billingService.resetWebsiteOperatorMfa(mfaResetOp.id);
            else await billingService.resetOperatorMfa(mfaResetOp.id);
            setMessage({ type: 'success', text: `Authenticator reset for "${mfaResetOp.username}" — they'll set it up again on next login` });
            setMfaResetOp(null);
            loadOperators();
            loadWebsiteOps();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to reset authenticator' });
        }
    };

    // ── Website operator (WEB_ADMIN) handlers ────────────────────────

    // Open the dialog and (lazily) fetch promotion candidates. We fetch
    // every time so the list reflects users added since the last open.
    const openCreateWebOp = () => {
        setCreateWebOpOpen(true);
        loadCandidates();
    };

    const handleSubmitWebsiteOperator = async () => {
        try {
            if (newWebOpMode === 'promote') {
                if (!promoteUserId) {
                    setMessage({ type: 'error', text: 'Please pick a user to promote' });
                    return;
                }
                const target = websiteCandidates.find((u) => String(u.id) === String(promoteUserId));
                await billingService.promoteToWebsiteOperator(promoteUserId);
                setMessage({ type: 'success', text: `Promoted ${target?.username || 'user'} to Website Operator` });
            } else {
                if (!newWebOp.username) {
                    setMessage({ type: 'error', text: 'Username is required' });
                    return;
                }
                if (newWebOpMode === 'invite' && !newWebOp.email) {
                    setMessage({ type: 'error', text: 'Email is required for invitation' });
                    return;
                }
                if (newWebOpMode === 'manual' && (!newWebOp.password || newWebOp.password.length < 6)) {
                    setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
                    return;
                }
                await billingService.createWebsiteOperator({
                    mode: newWebOpMode,
                    username: newWebOp.username,
                    email: newWebOp.email || undefined,
                    password: newWebOpMode === 'manual' ? newWebOp.password : undefined,
                });
                setMessage({ type: 'success', text: `Website operator "${newWebOp.username}" created` });
            }
            setCreateWebOpOpen(false);
            setNewWebOp({ username: '', email: '', password: '' });
            setPromoteUserId('');
            setNewWebOpMode('promote');
            loadWebsiteOps();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Operation failed' });
        }
    };

    const handleToggleWebsiteOp = async (op) => {
        try {
            await billingService.updateWebsiteOperator(op.id, { enabled: !op.enabled });
            loadWebsiteOps();
        } catch {
            setMessage({ type: 'error', text: 'Failed to update website operator' });
        }
    };

    const handleDeleteWebsiteOp = async () => {
        if (!deleteWebOp) return;
        try {
            await billingService.deleteWebsiteOperator(deleteWebOp.id);
            setMessage({ type: 'success', text: 'Website operator account deleted' });
            setDeleteWebOpOpen(false);
            setDeleteWebOp(null);
            loadWebsiteOps();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete website operator' });
        }
    };

    const handleDemoteWebsiteOp = async () => {
        if (!demoteWebOp) return;
        try {
            await billingService.demoteWebsiteOperator(demoteWebOp.id);
            setMessage({
                type: 'success',
                text: `${demoteWebOp.username} is no longer a Website Operator (demoted to ADMIN, account preserved)`,
            });
            setDemoteWebOpOpen(false);
            setDemoteWebOp(null);
            loadWebsiteOps();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to demote website operator' });
        }
    };

    const websiteOpColumns = [
        { field: 'username', headerName: 'Username', flex: 1 },
        { field: 'email', headerName: 'Email', flex: 1, renderCell: (p) => p.value || '-' },
        {
            field: 'enabled', headerName: 'Active', width: 90,
            renderCell: (p) => (
                <Switch checked={p.value} onChange={() => handleToggleWebsiteOp(p.row)} size="small" color="success" />
            ),
        },
        {
            field: 'lastLogin', headerName: 'Last Login', width: 140,
            renderCell: (p) => toServerDate(p.value)?.toLocaleDateString() ?? 'Never',
        },
        {
            field: 'createdAt', headerName: 'Created', width: 120,
            renderCell: (p) => toServerDate(p.value)?.toLocaleDateString() ?? '-',
        },
        {
            field: 'mfaEnabled', headerName: '2FA', width: 100, sortable: false,
            renderCell: (p) => p.value
                ? <Chip size="small" label="Enrolled" color="success" variant="outlined" />
                : <Chip size="small" label="Not set" variant="outlined" sx={{ opacity: 0.6 }} />,
        },
        {
            field: 'actions', headerName: '', width: 170, sortable: false,
            renderCell: (p) => (
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                    <Tooltip title="Edit email / password">
                        <IconButton size="small" onClick={() => openEditOperator(p.row)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={p.row.mfaEnabled ? 'Reset authenticator (2FA)' : 'No authenticator to reset'}>
                        <span>
                            <IconButton size="small" disabled={!p.row.mfaEnabled} onClick={() => setMfaResetOp(p.row)} sx={{ color: 'warning.main' }}>
                                <ResetMfaIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Remove role (keep account)">
                        <IconButton size="small" onClick={() => { setDemoteWebOp(p.row); setDemoteWebOpOpen(true); }} sx={{ color: 'warning.main' }}>
                            <DemoteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete account">
                        <IconButton size="small" onClick={() => { setDeleteWebOp(p.row); setDeleteWebOpOpen(true); }} sx={{ color: 'error.main' }}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    const operatorColumns = [
        { field: 'username', headerName: 'Username', flex: 1 },
        { field: 'email', headerName: 'Email', flex: 1, renderCell: (p) => p.value || '-' },
        {
            field: 'enabled', headerName: 'Active', width: 90,
            renderCell: (p) => (
                <Switch checked={p.value} onChange={() => handleToggleOperator(p.row)} size="small" color="success" />
            ),
        },
        {
            field: 'lastLogin', headerName: 'Last Login', width: 140,
            renderCell: (p) => toServerDate(p.value)?.toLocaleDateString() ?? 'Never',
        },
        {
            field: 'createdAt', headerName: 'Created', width: 120,
            renderCell: (p) => toServerDate(p.value)?.toLocaleDateString() ?? '-',
        },
        {
            field: 'mfaEnabled', headerName: '2FA', width: 100, sortable: false,
            renderCell: (p) => p.value
                ? <Chip size="small" label="Enrolled" color="success" variant="outlined" />
                : <Chip size="small" label="Not set" variant="outlined" sx={{ opacity: 0.6 }} />,
        },
        {
            field: 'actions', headerName: '', width: 130, sortable: false,
            renderCell: (p) => (
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                    <Tooltip title="Edit email / password">
                        <IconButton size="small" onClick={() => openEditOperator(p.row)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={p.row.mfaEnabled ? 'Reset authenticator (2FA)' : 'No authenticator to reset'}>
                        <span>
                            <IconButton size="small" disabled={!p.row.mfaEnabled} onClick={() => setMfaResetOp(p.row)} sx={{ color: 'warning.main' }}>
                                <ResetMfaIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => { setDeleteOp(p.row); setDeleteOpOpen(true); }} sx={{ color: 'error.main' }}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Security</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Configure captcha protection and manage platform operators.
                </Typography>
            </Box>

            {message.text && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
                    {message.text}
                </Alert>
            )}

            <Paper sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: 2 }}>
                    <Tab label="Captcha" />
                    <Tab label={`Operators (${operators.length})`} />
                    <Tab label={`Website Operators (${websiteOps.length})`} />
                </Tabs>

                {/* Captcha Tab */}
                {tab === 0 && (
                    <Box sx={{ p: 3 }}>
                        {captchaLoading ? (
                            <TableSkeleton rows={5} columns={4} />
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Captcha Configuration</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Enabled</Typography>
                                        <Switch checked={captcha.enabled} onChange={(e) => setCaptcha({ ...captcha, enabled: e.target.checked })} color="success" />
                                    </Box>
                                </Box>

                                <FormControl fullWidth>
                                    <InputLabel>Provider</InputLabel>
                                    <Select value={captcha.provider} onChange={(e) => setCaptcha({ ...captcha, provider: e.target.value })} label="Provider">
                                        {CAPTCHA_PROVIDERS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                                    </Select>
                                </FormControl>

                                <TextField
                                    label="Site Key" value={captcha.siteKey}
                                    onChange={(e) => setCaptcha({ ...captcha, siteKey: e.target.value })}
                                    fullWidth InputLabelProps={{ shrink: true }}
                                    helperText="Public key used in the frontend widget"
                                />

                                <TextField
                                    label="Secret Key"
                                    value={captcha.secretKey}
                                    onChange={(e) => setCaptcha({ ...captcha, secretKey: e.target.value })}
                                    type={showSecret ? 'text' : 'password'}
                                    fullWidth InputLabelProps={{ shrink: true }}
                                    helperText="Private key used for server-side verification"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setShowSecret(!showSecret)}>
                                                    {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {(captcha.provider === 'RECAPTCHA_V3' || captcha.provider === 'TURNSTILE') && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Minimum Score: {captcha.minScore}
                                        </Typography>
                                        <Slider
                                            value={captcha.minScore} min={0} max={1} step={0.1}
                                            onChange={(_, v) => setCaptcha({ ...captcha, minScore: v })}
                                            valueLabelDisplay="auto"
                                        />
                                    </Box>
                                )}

                                <Button
                                    variant="contained" startIcon={<SaveIcon />}
                                    onClick={handleSaveCaptcha} disabled={captchaSaving}
                                    sx={{ alignSelf: 'flex-start' }}
                                >
                                    {captchaSaving ? 'Saving...' : 'Save Captcha Settings'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Platform Operators Tab */}
                {tab === 1 && (
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Platform Operators ({operators.length})
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadOperators}>Refresh</Button>
                                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreateOpOpen(true)}>Create Operator</Button>
                            </Box>
                        </Box>

                        {operatorsLoading ? (
                            <TableSkeleton rows={5} columns={4} />
                        ) : (
                            <DataGrid
                                rows={operators} columns={operatorColumns} autoHeight pageSize={10}
                                disableRowSelectionOnClick getRowId={(row) => row.id}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-cell': { borderColor: theme.palette.divider },
                                    '& .MuiDataGrid-columnHeaders': { borderColor: theme.palette.divider, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' },
                                }}
                            />
                        )}
                    </Box>
                )}
                {/* Website Operators Tab — WEB_ADMIN role scoped to the
                    default org's website surface only. */}
                {tab === 2 && (
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Website Operators ({websiteOps.length})
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                                        Sub-operators that can sign in to the operator panel and only access
                                        <strong> Website Controls </strong>
                                        (Waitlist, Documentation, Contact Inbox). They live in the
                                        <code style={{ marginLeft: 4, marginRight: 4 }}>default</code>
                                        organisation and have no platform-side access.
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                    <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadWebsiteOps}>Refresh</Button>
                                    <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreateWebOp}>
                                        Add Website Operator
                                    </Button>
                                </Box>
                            </Box>
                        </Box>

                        {websiteOpsLoading ? (
                            <TableSkeleton rows={5} columns={4} />
                        ) : websiteOps.length === 0 ? (
                            <Box sx={{
                                p: 4,
                                textAlign: 'center',
                                border: `1px dashed ${theme.palette.divider}`,
                                borderRadius: 1.5,
                            }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    No website operators yet. Create one to delegate marketing-surface admin
                                    (waitlist, docs, contact inbox) without granting full platform access.
                                </Typography>
                                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreateWebOp}>
                                    Add your first website operator
                                </Button>
                            </Box>
                        ) : (
                            <DataGrid
                                rows={websiteOps} columns={websiteOpColumns} autoHeight pageSize={10}
                                disableRowSelectionOnClick getRowId={(row) => row.id}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-cell': { borderColor: theme.palette.divider },
                                    '& .MuiDataGrid-columnHeaders': { borderColor: theme.palette.divider, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' },
                                }}
                            />
                        )}
                    </Box>
                )}
            </Paper>

            {/* Create Operator Dialog */}
            <Dialog open={createOpOpen} onClose={() => setCreateOpOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create Operator</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <RadioGroup row value={newOpMode} onChange={(e) => { setNewOpMode(e.target.value); setNewOp({ username: '', email: '', password: '' }); }}>
                            <FormControlLabel value="manual" control={<Radio size="small" />} label="Create Manually" />
                            <FormControlLabel value="invite" control={<Radio size="small" />} label="Invite by Email" />
                        </RadioGroup>
                        <TextField label="Username" value={newOp.username} onChange={(e) => setNewOp({ ...newOp, username: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} required />
                        <TextField label="Email" type="email" value={newOp.email} onChange={(e) => setNewOp({ ...newOp, email: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} required={newOpMode === 'invite'} helperText={newOpMode === 'invite' ? 'Invitation will be sent to this email' : 'Optional'} />
                        {newOpMode === 'manual' && (
                            <TextField label="Password" type="password" value={newOp.password} onChange={(e) => setNewOp({ ...newOp, password: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} required helperText="Minimum 6 characters" />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setCreateOpOpen(false); setNewOp({ username: '', email: '', password: '' }); }}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateOperator} disabled={!newOp.username}>
                        {newOpMode === 'invite' ? 'Send Invitation' : 'Create Operator'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Operator Dialog */}
            <Dialog open={deleteOpOpen} onClose={() => setDeleteOpOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'error.main' }}>Delete Operator</DialogTitle>
                <DialogContent>
                    <Alert severity="error">
                        Permanently delete operator <strong>{deleteOp?.username}</strong>?
                        This cannot be undone.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteOperator}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Operator (email / password) — works for both operator flavours */}
            <Dialog open={Boolean(editOp)} onClose={() => setEditOp(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Edit {editOp && isWebOp(editOp) ? 'website operator' : 'operator'}
                    {editOp ? ` — ${editOp.username}` : ''}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth margin="normal" label="Email" type="email"
                        value={editOpForm.email}
                        onChange={(e) => setEditOpForm({ ...editOpForm, email: e.target.value })}
                    />
                    <TextField
                        fullWidth margin="normal" label="New password"
                        placeholder="Leave blank to keep current password"
                        type={editShowPw ? 'text' : 'password'}
                        value={editOpForm.password}
                        onChange={(e) => setEditOpForm({ ...editOpForm, password: e.target.value })}
                        helperText="Set a new password (min 6 chars), or leave blank."
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setEditShowPw((v) => !v)}>
                                        {editShowPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOp(null)}>Cancel</Button>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveOperator}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Reset authenticator (2FA) confirm */}
            <Dialog open={Boolean(mfaResetOp)} onClose={() => setMfaResetOp(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'warning.main' }}>Reset authenticator</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        Reset the authenticator (2FA) for <strong>{mfaResetOp?.username}</strong>?
                        Their current authenticator app will stop working, and they'll be walked
                        through setting up a fresh one (scan a new QR code) on their next login.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMfaResetOp(null)}>Cancel</Button>
                    <Button variant="contained" color="warning" startIcon={<ResetMfaIcon />} onClick={handleResetMfa}>
                        Reset authenticator
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Website Operator Dialog — three modes:
                  • promote — pick an existing default-org user
                  • manual  — create a fresh account with a password
                  • invite  — create a fresh account, mail them a set-password link */}
            <Dialog
                open={createWebOpOpen}
                onClose={() => { setCreateWebOpOpen(false); setNewWebOp({ username: '', email: '', password: '' }); setPromoteUserId(''); }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add Website Operator</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Website Operators sign in to the same operator panel but only see <strong>Website Controls</strong>
                        {' '}(Waitlist, Documentation, Contact Inbox). They live in the <code>default</code> organisation
                        and cannot reach Platform Controls or any tenant data.
                    </Alert>

                    {/* Mode picker — visual cards instead of radios so
                        the differences land at a glance. */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mt: 1, mb: 2 }}>
                        {[
                            { value: 'promote', label: 'Promote', sub: 'Existing user', icon: <PromoteIcon /> },
                            { value: 'manual',  label: 'Manual',  sub: 'Set password',  icon: <AddIcon /> },
                            { value: 'invite',  label: 'Invite',  sub: 'Email link',    icon: <TeamIcon /> },
                        ].map((m) => (
                            <Box
                                key={m.value}
                                onClick={() => setNewWebOpMode(m.value)}
                                role="button"
                                sx={{
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    border: '2px solid',
                                    borderColor: newWebOpMode === m.value ? 'primary.main' : theme.palette.divider,
                                    bgcolor: newWebOpMode === m.value
                                        ? (isDark ? 'rgba(110,168,254,0.10)' : 'rgba(9,105,218,0.06)')
                                        : 'transparent',
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    '&:hover': { borderColor: 'primary.main' },
                                }}
                            >
                                <Box sx={{ color: newWebOpMode === m.value ? 'primary.main' : 'text.secondary', mb: 0.5 }}>
                                    {m.icon}
                                </Box>
                                <Typography variant="body2" fontWeight={700}>
                                    {m.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {m.sub}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {newWebOpMode === 'promote' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel id="promote-user">User to promote</InputLabel>
                                <Select
                                    labelId="promote-user"
                                    label="User to promote"
                                    value={promoteUserId}
                                    onChange={(e) => setPromoteUserId(e.target.value)}
                                    disabled={candidatesLoading}
                                >
                                    {candidatesLoading && <MenuItem value="" disabled>Loading…</MenuItem>}
                                    {!candidatesLoading && websiteCandidates.length === 0 && (
                                        <MenuItem value="" disabled>
                                            No eligible users — every default-org member is already a Website Operator or a Platform Operator
                                        </MenuItem>
                                    )}
                                    {websiteCandidates.map((u) => (
                                        <MenuItem key={u.id} value={u.id}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {u.username}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {u.email || 'no email'} · {u.role}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                                Promoting a user changes their role from <code>{websiteCandidates.find((u) => String(u.id) === String(promoteUserId))?.role || 'their current role'}</code> to <code>WEB_ADMIN</code>.
                                They keep their username, password, and login history — but they lose access to platform-side surfaces. Demote any time from the row's "Remove role" action.
                            </Alert>
                        </Box>
                    )}

                    {newWebOpMode !== 'promote' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Username"
                                value={newWebOp.username}
                                onChange={(e) => setNewWebOp({ ...newWebOp, username: e.target.value })}
                                fullWidth InputLabelProps={{ shrink: true }} required
                            />
                            <TextField
                                label="Email" type="email"
                                value={newWebOp.email}
                                onChange={(e) => setNewWebOp({ ...newWebOp, email: e.target.value })}
                                fullWidth InputLabelProps={{ shrink: true }}
                                required={newWebOpMode === 'invite'}
                                helperText={newWebOpMode === 'invite' ? 'Invitation will be sent to this email' : 'Optional'}
                            />
                            {newWebOpMode === 'manual' && (
                                <TextField
                                    label="Password" type="password"
                                    value={newWebOp.password}
                                    onChange={(e) => setNewWebOp({ ...newWebOp, password: e.target.value })}
                                    fullWidth InputLabelProps={{ shrink: true }} required
                                    helperText="Minimum 6 characters"
                                />
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setCreateWebOpOpen(false); setNewWebOp({ username: '', email: '', password: '' }); setPromoteUserId(''); }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitWebsiteOperator}
                        disabled={
                            (newWebOpMode === 'promote' && !promoteUserId) ||
                            (newWebOpMode !== 'promote' && !newWebOp.username)
                        }
                    >
                        {newWebOpMode === 'promote'
                            ? 'Promote user'
                            : newWebOpMode === 'invite'
                                ? 'Send Invitation'
                                : 'Create Website Operator'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Demote (remove role, keep account) Dialog */}
            <Dialog open={demoteWebOpOpen} onClose={() => setDemoteWebOpOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Remove Website Operator role</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        Strip the Website Operator role from <strong>{demoteWebOp?.username}</strong>?
                        <br /><br />
                        Their account stays intact (username, password, email, login history). Their role
                        becomes <code>ADMIN</code> in the default organisation, so they can still sign in
                        and use the platform — they just won't see Website Controls in the operator panel.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDemoteWebOpOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={handleDemoteWebsiteOp}>Remove role</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Website Operator Dialog */}
            <Dialog open={deleteWebOpOpen} onClose={() => setDeleteWebOpOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'error.main' }}>Delete Website Operator</DialogTitle>
                <DialogContent>
                    <Alert severity="error">
                        Permanently delete website operator <strong>{deleteWebOp?.username}</strong>?
                        This cannot be undone.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteWebOpOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteWebsiteOp}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SecurityPage;
